import axios from "axios";
import getConfig from "next/config";
import {
  CreateApplicationResponse,
  LoginWithToken,
  GenericResponse,
  SubmitExpenditure,
  SubmitIncome,
  SubmitProperty,
  SubmitPreferredProperty,
  SubmitQualifyingCriteria,
  CreateApplication,
  Application,
  NewApplicant,
  AddApplicantResponse,
  RemoveApplicant,
  SubmitPersonalDetails,
  VerificationResponse,
  LoginWithPassword,
  SendMessage,
  SubmitPropertyAddress,
  SubmitPropertyAgent,
  DetailedProperty,
  SubmitPropertyType,
  SubmitLeaseType,
  SubmitPropertyInformation,
  BaseRequest,
  GetApplicantResponse,
  SubmitApplicationOffer,
  CurrentUserResponse,
  SubmitCreditAndStoreCards,
  SubmitDependants,
  SubmitLoansAndMortgages,
  SubmitAddressHistory,
  ResetPassword,
  SubmitDetailedIncome,
  ApplicantPreferences,
  DeleteIncome,
  GoogleLocationResponse,
  UpdateApplicationStatus,
  SubmitPropertyForReview,
  CalculateBudget,
  CalculateDealBudget,
  AcceptQuote,
  BudgetInPrinciple,
  PropertyPaymentDetails,
  GooglePlaceDetailsResponse,
  Documents,
  CreateLandlordMondayID,
  createLandlordMondayIDResponse,
  LandlordProperty,
  GetReferralsResponse,
  Listings,
  Listing,
  GetListings,
  UpdateApplicantMainDetails,
  Applicant,
  ListingSearchSuggestion,
  TruelayerAuthLinkResponse,
  ConnectedAccount,
  IdentityVerificationStatus,
  TruelayerSyncLog,
  ListingGeometry,
  ListingMapMetaData,
  BookingAvailableSlotsResponse,
  CreateBookingSchemaRequest,
  BookingDetailsSchema,
  AuthUser,
  GetRentalApplicantResponse,
  RentalApplicantAddresses,
  RentalApplicantIncome,
  CreateRentalApplicantRequest,
  UpdateRentalApplicationRequest,
  UpdateBookingSchemaRequest,
  VideoGuide,
} from "./apiTypes";
import Router from "next/router";
import { queryClient } from "./queryClient";
import routes from "@/constants/routes";
import {
  RentalApplicantIdentityVerification,
  RentalApplication,
  RentalApplicationOfferDetails,
} from "./apiTypes/RentalApplication.type";
import { RentalApplicant } from "./apiTypes/RentalApplicant.type";
import { RentalApplicantExpenditureFormData, Viewing } from "./cms/apiTypes";
import { RentalApplicationEligibilityProps } from "@/components/dashboard/RentalApplicant/RentalApplicationEligibility";
import { RentalApplicationDocument } from "./apiTypes/Document";
import { RentalApplicationMoveInSummary } from "./apiTypes/MoveInSummary.type";
import { removeSessionData } from "@/helpers/session";

const { publicRuntimeConfig } = getConfig();

const axiosInstance = axios.create({
  baseURL: publicRuntimeConfig.NEXT_PUBLIC_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// API returns 200 with "$success: false" instead of http 404 code for
// resources that are not found (feature request from the client), so we
// want to manually fail requests on client side when we see failed request.
axiosInstance.interceptors.response.use((res) => {
  if (res.data.$success === false) {
    throw new Error("Request failed");
  }

  return res;
});

axiosInstance.interceptors.request.use((req) => {
  // if (res.data.$success === false) {
  //   throw new Error('Request failed');
  // }
  try {
    const token = localStorage.getItem("token");
    if (token && req.headers && !req.headers["Authorization"]) {
      req.headers["Authorization"] = token;
    }
  } catch (error) {}

  return req;
});

export const createApplication = (data: CreateApplication): Promise<CreateApplicationResponse> =>
  axiosInstance.post("/web/v1/applications", data).then((res) => {
    !!res.data && localStorage.setItem("token", res.data.actionToken.value);
    return res.data;
  });

export const submitPersonalDetails = ({
  applicationId,
  applicantId,
  data,
}: SubmitPersonalDetails): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}/personal-details`, data)
    .then((res) => res.data);

export const submitAddressHistory = ({
  applicationId,
  applicantId,
  data,
}: SubmitAddressHistory): Promise<GenericResponse> =>
  axiosInstance
    .post(
      `/web/v1/applications/${applicationId}/applicants/${applicantId}/historical-addresses`,
      data,
    )
    .then((res) => res.data);

export const submitDetailedIncome = ({
  applicationId,
  applicantId,
  data,
}: SubmitDetailedIncome): Promise<GenericResponse> =>
  axiosInstance
    .post(`/web/v1/applications/${applicationId}/applicants/${applicantId}/income/sources`, data)
    .then((res) => res.data);

export const getApplicant = (
  applicationId: string,
  applicantId: string,
): Promise<GetApplicantResponse & GenericResponse> =>
  axiosInstance.get(`/applicants/${applicantId}`).then((res) => res.data);

export const getBrokerReferral = (
  referralId: string,
): Promise<GetApplicantResponse & GenericResponse> =>
  axiosInstance.get(`/broker-referrals/${referralId}`).then((res) => res.data);

export const getGooglePlaceDetails = (
  placeId: string,
): Promise<GooglePlaceDetailsResponse & GenericResponse> =>
  axiosInstance
    .get(`/web/v1/applications/map/google-place-details?placeId=${placeId}`)
    .then((res) => res.data);

export const submitApplicant = ({
  applicationId,
  data,
}: NewApplicant): Promise<AddApplicantResponse & GenericResponse> =>
  axiosInstance
    .post(`/web/v1/applications/${applicationId}/applicants`, data)
    .then((res) => res.data);

export const updateApplicantMainDetails = ({
  applicationId,
  applicantId,
  data,
}: UpdateApplicantMainDetails): Promise<Applicant & GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}`, data)
    .then((res) => res.data);

export const acceptQuote = ({
  applicationId,
}: AcceptQuote): Promise<AddApplicantResponse & GenericResponse> =>
  axiosInstance.post(`/applications/${applicationId}/accept-quote`).then((res) => res.data);

export const acceptOffer = ({
  applicationId,
}: AcceptQuote): Promise<AddApplicantResponse & GenericResponse> =>
  axiosInstance.post(`/applications/${applicationId}/accept-offer`).then((res) => res.data);

export const deleteApplicant = ({
  applicationId,
  applicantId,
}: RemoveApplicant): Promise<AddApplicantResponse & GenericResponse> =>
  axiosInstance.delete(`/applicants/${applicantId}`).then((res) => res.data);

export const submitExpenditure = ({
  applicationId,
  applicantId,
  data,
}: SubmitExpenditure): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}/expenditure`, data)
    .then((res) => res.data);

export const submitIncome = ({
  applicationId,
  applicantId,
  data,
}: SubmitIncome): Promise<GenericResponse> =>
  axiosInstance
    .post(`/web/v1/applications/${applicationId}/applicants/${applicantId}/income`, data)
    .then((res) => res.data);

export const submitQualifyingCriteria = ({
  applicationId,
  applicantId,
  data,
}: SubmitQualifyingCriteria): Promise<GenericResponse> =>
  axiosInstance
    .patch(
      `/web/v1/applications/${applicationId}/applicants/${applicantId}/qualifying-criteria`,
      data,
    )
    .then((res) => res.data);

export const checkQualifyingCriteria = ({
  applicationId,
  data: {},
}: {
  applicationId: string;
  data: any;
}): Promise<Application> =>
  axiosInstance.post(`/applications/${applicationId}/qualifying-criteria`).then((res) => res.data);

export const submitCreditAndStoreCards = ({
  applicationId,
  applicantId,
  data,
}: SubmitCreditAndStoreCards): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}/expenditure`, data)
    .then((res) => res.data);

export const submitLoansAndMortgages = ({
  applicationId,
  applicantId,
  data,
}: SubmitLoansAndMortgages): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}/expenditure`, data)
    .then((res) => res.data);

export const submitDependants = ({
  applicationId,
  applicantId,
  data,
}: SubmitDependants): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/applicants/${applicantId}/expenditure`, data)
    .then((res) => res.data);

export const submitProperty = ({ applicationId, data }: SubmitProperty): Promise<GenericResponse> =>
  axiosInstance
    .patch(`/web/v1/applications/${applicationId}/property`, data)
    .then((res) => res.data);

export const submitPreferredProperty = ({
  applicationId,
  data,
  skipAppProcess,
}: SubmitPreferredProperty): Promise<GenericResponse> =>
  axiosInstance
    .patch(
      `/applications/${applicationId}/preferred-property?skipAppProcess=${skipAppProcess ? 1 : 0}`,
      data,
    )
    .then((res) => res.data);

export const getApplication = (): Promise<Application> =>
  axiosInstance.get(`/web/v1/applications/current`).then((res) => res.data);

export const getApplicationWithToken = ({
  applicationId,
  token,
}: {
  token: string;
  applicationId: string;
}): Promise<Application> => {
  const axiosInstance = axios.create({
    baseURL: publicRuntimeConfig.NEXT_PUBLIC_API_URL,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  });
  return axiosInstance.get(`/applications/${applicationId}`).then((res) => {
    return res.data;
  });
};

export const getBudgetById = ({ budgetId }: { budgetId: string }): Promise<BudgetInPrinciple> =>
  axiosInstance.get(`/budget-in-principle/${budgetId}`).then((res) => {
    return res.data;
  });

export const shareBudget = ({
  applicationId,
  email,
}: {
  applicationId: string;
  email: string;
}): Promise<string> =>
  axiosInstance
    .post(`/applications/${applicationId}/share-budget`, { email: email })
    .then((res) => {
      return res.data;
    });

export const loginWithToken = (data: LoginWithToken): Promise<string> =>
  axiosInstance.post("/web/v1/auth/login/with-token", data).then((res) => {
    res?.data && localStorage.setItem("token", res.data.authToken.value);
    return res.data;
  });

export const getDocuments = (applicationId: string, condition: string): Promise<Documents> =>
  axiosInstance
    .get(`/applications/${applicationId}/documents?${condition}`)
    .then((res) => res.data);

export const downloadDocuments = (applicationId: string, documentId: string): Promise<any> =>
  axiosInstance
    .get(`/applications/${applicationId}/documents/${documentId}/download`, {
      responseType: "arraybuffer",
    })
    .then((res) => {
      return res;
    });

export const getApplicationReferrals = (applicationId: string): Promise<GetReferralsResponse> =>
  axiosInstance.get(`/applications/${applicationId}/referrals`).then((res) => res.data);

export const getProperty = (propertyId: string): Promise<DetailedProperty> =>
  axiosInstance.get(`/properties/${propertyId}`).then((res) => res.data);

export const getPropertyPaymentDetails = (propertyId: string): Promise<PropertyPaymentDetails> =>
  axiosInstance.get(`/properties/${propertyId}/payment-detail`).then((res) => {
    return res.data;
  });

export const submitPropertyAddress = ({
  property_id,
  data,
}: SubmitPropertyAddress): Promise<GenericResponse> =>
  axiosInstance.post(`/properties/`, data).then((res) => res.data);

export const updatePropertyAddress = ({
  property_id,
  data,
}: SubmitPropertyAddress): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const submitPropertyAgent = ({
  property_id,
  data,
}: SubmitPropertyAgent): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const submitPropertyType = ({
  property_id,
  data,
}: SubmitPropertyType): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const submitPropertyForReview = ({
  property_id,
}: SubmitPropertyForReview): Promise<GenericResponse> =>
  axiosInstance.post(`/properties/${property_id}/submit`, {}).then((res) => res.data);

export const submitLeaseType = ({ property_id, data }: SubmitLeaseType): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const submitPropertyInformation = ({
  property_id,
  data,
}: SubmitPropertyInformation): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const submitApplicationOffer = ({
  property_id,
  data,
}: SubmitApplicationOffer): Promise<GenericResponse> =>
  axiosInstance.put(`/properties/${property_id}`, data).then((res) => res.data);

export const loginWithPassword = async (data: LoginWithPassword): Promise<string> => {
  try {
    const res = await axiosInstance.post("/web/v1/auth/login", data);
    !!res.data && localStorage.setItem("token", res.data.authToken.value);
    return res.data;
  } catch (err) {
    throw new Error(
      "We don’t recognise either the email address or password. Please try again or reset your password.",
    );
  }
};

export const logout = (): Promise<void> => {
  localStorage.removeItem("token");
  return axiosInstance.delete("/web/v1/auth/logout");
};

export const createPassword = (password: string): Promise<string> =>
  axiosInstance.post(`/web/v1/auth/set-password`, { password }).then((res) => res.data);

export const forgotPassword = (email: string): Promise<GenericResponse> =>
  axiosInstance.post(`/web/v1/auth/forgot-password`, { email }).then((res) => res.data);

export const resetPassword = (data: ResetPassword): Promise<GenericResponse> =>
  axiosInstance.post(`/web/v1/auth/reset-password`, data).then((res) => res.data);

export const getCurrentUser = (): Promise<CurrentUserResponse> =>
  axiosInstance.get(`/web/v1/auth/me`).then((res) => res.data);

export const generateVeriffUrl = (
  applicantId: string,
): Promise<VerificationResponse & GenericResponse> =>
  axiosInstance
    .post(`/web/v1/identity-verification/generate-veriff-url/${applicantId}`)
    .then((res) => res.data);

export const generateIncomeVerificationUrl = ({
  applicationId,
  applicantId,
}: BaseRequest): Promise<VerificationResponse & GenericResponse> =>
  axiosInstance
    .post(`/web/v1/applications/${applicationId}/applicants/${applicantId}/account-score-url`)
    .then((res) => res.data);

export const generateFinternUrl = ({
  applicantId,
}: BaseRequest): Promise<VerificationResponse & GenericResponse> =>
  axiosInstance.post(`/fintern/create-fintern-url/${applicantId}`).then((res) => res.data);

export const sendMessage = (data: SendMessage): Promise<GenericResponse> =>
  axiosInstance.post(`/web/v1/contact`, data).then((res) => res.data);

export const updateApplicantPreferences = (data: SendMessage): Promise<GenericResponse> =>
  axiosInstance.post(`/web/v1/contact`, data).then((res) => res.data);

export const updateApplicantsPreferences = ({
  applicationId,
  applicantId,
  data,
}: ApplicantPreferences): Promise<GenericResponse> =>
  axiosInstance
    .post(`/web/v1/applications/${applicationId}/applicants/${applicantId}/preferences`, data)
    .then((res) => res.data);

export const deleteIncomeSource = ({
  applicationId,
  applicantId,
  data,
}: DeleteIncome): Promise<GenericResponse> =>
  axiosInstance
    .delete(`/web/v1/applications/${applicationId}/applicants/${applicantId}/income`, { data })
    .then((res) => res.data);

export const getGoogleLocationData = (placeId: string): Promise<GoogleLocationResponse> =>
  axiosInstance
    .get(`/web/v1/applications/map/google-place-details?placeId=${placeId}`)
    .then((res) => res.data);

export const calculateBudget = ({
  type,
  incomeOrPrice,
  years,
  outcode,
  estimatedHousePriceGrowth,
  estimatedIncomeGrowth,
}: CalculateBudget): Promise<any> =>
  axiosInstance
    .get(
      `/web/budget?type=${type}&incomeOrPrice=${incomeOrPrice}&years=${years}&outcode=${outcode}&estimatedHousePriceGrowth=${estimatedHousePriceGrowth}&estimatedIncomeGrowth=${estimatedIncomeGrowth}`,
    )
    .then((res) => res.data);

export const calculateDealBudget = ({
  postcode,
  propertyValue,
  currentRent,
  managedByAgent,
  estimatedHousePriceGrowth,
  years,
}: CalculateDealBudget): Promise<any> =>
  axiosInstance
    .get(
      `/landlord/calculate-budget?postcode=${postcode}&propertyValue=${propertyValue}&currentRent=${currentRent}&managedByAgent=${managedByAgent}&estimatedHousePriceGrowth=${estimatedHousePriceGrowth}&years=${years}`,
    )
    .then((res) => res.data);

export const updateApplicationStatus = ({
  applicationId,
  data,
}: UpdateApplicationStatus): Promise<GenericResponse> =>
  axiosInstance.post(`/applications/${applicationId}/submit`, data).then((res) => res.data);

export const createLandlordMondayID = ({
  data,
}: CreateLandlordMondayID): Promise<createLandlordMondayIDResponse> =>
  axiosInstance.post(`/landlords`, data).then((res) => res.data);

export const landlordPropertyToMonday = ({
  landlordMondayId,
  data,
}: LandlordProperty): Promise<GenericResponse> =>
  axiosInstance.post(`/landlords/${landlordMondayId}/property`, data).then((res) => res.data);

export const getLandlord = (email: string): Promise<any> =>
  axiosInstance.get(`/landlords?email=${encodeURIComponent(email)}`).then((res) => res.data);

export const getListings = async ({
  limit,
  offset,
  search_term,
  neighbourhood,
  bedrooms,
  bathrooms,
  availability,
  parkingSpace,
  sortDirection,
  sortField,
}: GetListings): Promise<Listings> => {
  const params = new URLSearchParams();

  params.append("limit", String(limit));
  params.append("offset", String(offset));

  if (bedrooms != null) params.append("bedrooms", String(bedrooms));
  if (bathrooms != null) params.append("bathrooms", String(bathrooms));
  if (availability) params.append("availability_status", availability);

  if (sortField && sortDirection) {
    params.append("sort_field", sortField);
    params.append("sort_direction", sortDirection);
  }

  if (search_term) params.append("search_term", search_term);
  if (neighbourhood) params.append("neighbourhood", neighbourhood);

  if (parkingSpace != null) params.append("parking_space", String(parkingSpace));

  const url = `/listings?${params.toString()}`;

  return axiosInstance.get(url).then((res) => {
    return { ...res.data, limit, offset } as Listings;
  });
};

export const getListingItem = (id: string): Promise<Listing> =>
  axiosInstance.get(`/listings/${id}`).then((res) => {
    return res.data;
  });

export const getListingSearchSuggestions = ({
  search,
}: {
  search: string;
}): Promise<ListingSearchSuggestion[]> =>
  axiosInstance.get(`/listings/auto-complete/terms?search=${search}`).then((res) => {
    return res.data;
  });

export const generateTrueLayerIncomeVerificationUrl = ({
  name,
  dob,
  applicantId,
}: {
  name: string;
  dob: string;
  applicantId: string;
}): Promise<TruelayerAuthLinkResponse | undefined> =>
  axiosInstance
    .post(`/truelayer/bank-auth-link`, { name, dob, applicant_id: applicantId })
    .then((res) => res.data);

export const fetchTruelayeToken = ({
  code,
  userId,
}: {
  code: string;
  userId: string;
}): Promise<GenericResponse & { log_id: string }> =>
  axiosInstance
    .post(`/truelayer/oauth/callback`, { code, user_id: userId })
    .then((res) => res.data);

export const fetchTruelayerConnectedAccounts = ({
  userId,
}: {
  userId: string;
}): Promise<ConnectedAccount[]> =>
  axiosInstance.get(`/truelayer/connected-accounts?user_id=${userId}`).then((res) => res.data);

export const fetchTruelayerAuthLink = ({
  userId,
}: {
  userId: string;
}): Promise<TruelayerAuthLinkResponse> =>
  axiosInstance.get(`/truelayer/auth-link?user_id=${userId}`).then((res) => res.data);

export const fetchIdentityVerificationStatus = ({
  applicantId,
}: {
  applicantId: string;
}): Promise<IdentityVerificationStatus> =>
  axiosInstance
    .get(`/apply/id-verification-status`, { params: { applicant_id: applicantId } })
    .then((res) => res.data);

export const fetchTruelayerLogStatus = ({
  userId,
  logId,
}: {
  userId: string;
  logId: string;
}): Promise<TruelayerSyncLog> =>
  axiosInstance.get(`/users/${userId}/truelayer/sync-logs/${logId}`).then((res) => res.data);

export const sendReferralLinkEmail = ({
  email,
}: {
  email: string;
}): Promise<{ success: boolean }> =>
  axiosInstance.post(`/referral/send-referral-link-email`, { email }).then((res) => res.data);

export const getListingsGeometry = ({
  districtCode,
  bedrooms,
  bathrooms,
  availability,
}: {
  districtCode?: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  availability?: "available" | "unavailable" | "unreserved" | null;
}): Promise<ListingGeometry> => {
  const params: Record<string, any> = {};

  if (districtCode) params.district_code = districtCode;
  if (bedrooms != null) params.bedrooms = bedrooms;
  if (bathrooms != null) params.bathrooms = bathrooms;
  if (availability) params.availability_status = availability;

  return axiosInstance.get("/listings-geometry", { params }).then((res) => res.data);
};

export const getListingMapMetaData = (ids: number[]): Promise<{ result: ListingMapMetaData[] }> => {
  const url = `/listings/map/meta?listingIds=${ids.join(",")}`;
  return axiosInstance.get(url).then((res) => {
    return res.data;
  });
};

export const getSimilarListings = (mondayId: string): Promise<{ result: Listing[] }> => {
  return axiosInstance.get(`/listing/${mondayId}/fetch-similar`).then((res) => {
    return res.data;
  });
};

export const getBookingAvailableSlots = (data: {
  slug: string;
  startDate?: string;
  endDate?: string;
}): Promise<BookingAvailableSlotsResponse> => {
  const params = new URLSearchParams({
    slug: data.slug,
    ...(data.startDate && { start_date: data.startDate }),
    ...(data.endDate && { end_date: data.endDate }),
  });

  return axiosInstance.get(`/bookings/availability/?${params.toString()}`).then((res) => res.data);
};

// v3 booking routes also move the MS Bookings appointment when the time changes
// and reject unavailable slots with 409 SLOT_UNAVAILABLE.
export const isBookingV3Enabled = (): boolean =>
  getConfig()?.publicRuntimeConfig?.NEXT_ENABLE_BOOKING_V3 === "1";

export const createBooking = (
  data: CreateBookingSchemaRequest,
): Promise<{
  mondayId: string;
  isIncomeEligible: boolean;
  isExistingBooking: boolean;
  bookingToken?: string;
}> => {
  const url = isBookingV3Enabled() ? `/v3/bookings` : `/bookings`;
  return axiosInstance.post(url, data).then((res) => res.data);
};

export const getBookingDetails = (
  bookingMondayId: string,
  bookingToken?: string,
): Promise<BookingDetailsSchema> => {
  return axiosInstance
    .get(`/bookings/${bookingMondayId}`, {
      headers: bookingToken ? { Authorization: `Bearer ${bookingToken}` } : undefined,
    })
    .then((res) => res.data.booking);
};

export const generateMagicLink = async (email: string, redirect?: string): Promise<void> => {
  await axiosInstance.post("/web/v2/auth/magic-link/generate", { email, redirect });
};

export const verifyMagicLink = async (
  token: string,
  email: string,
): Promise<{ success: boolean; application_type: "core" | "rental" | null }> => {
  const res = await axiosInstance.post("/web/v2/auth/magic-link/verify", { token, email });
  return res.data;
};

export const checkApplicationReady = async (email: string): Promise<{ ready: boolean }> => {
  const res = await axiosInstance.get(
    `/web/v2/auth/application-ready?email=${encodeURIComponent(email)}`,
  );
  return res.data;
};

export const autoLogin = async (
  email: string,
): Promise<{ success: boolean; application_type: string }> => {
  const res = await axiosInstance.post("/web/v2/auth/auto-login", { email });
  return res.data;
};

export const getAuthUser = async (): Promise<AuthUser> => {
  const { data } = await axiosInstance.get<AuthUser>("/web/v2/auth/me");
  return data;
};

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post("/web/v2/auth/logout");
  } finally {
    queryClient.clear();
    if (typeof window !== "undefined") {
      removeSessionData("booking");
      localStorage.removeItem("token");
      if (!window.location.pathname.includes(routes.dashboard.login)) {
        Router.replace(routes.dashboard.login);
      }
    }
  }
};

export const updateBooking = (
  bookingMondayId: string,
  data: Partial<CreateBookingSchemaRequest>,
  bookingToken?: string,
): Promise<UpdateBookingSchemaRequest> => {
  const headers = bookingToken ? { Authorization: `Bearer ${bookingToken}` } : undefined;

  if (isBookingV3Enabled()) {
    // v3 renames `create_ms_booking` to `complete`.
    const { create_ms_booking, ...rest } = data;
    return axiosInstance
      .patch(
        `/v3/bookings/${bookingMondayId}`,
        { name: rest.name, data: rest.data, complete: create_ms_booking },
        { headers },
      )
      .then((res) => res.data);
  }

  return axiosInstance
    .patch(`/bookings/update/${bookingMondayId}`, data, { headers })
    .then((res) => res.data);
};

export const getRentalApplication = async (applicationId: string): Promise<RentalApplication> => {
  const { data } = await axiosInstance.get<RentalApplication>(
    `/rental-applications/${applicationId}`,
  );
  return data;
};

export const getRentalApplicants = async (applicationId: string): Promise<RentalApplicant[]> => {
  const { data } = await axiosInstance.get<RentalApplicant[]>(
    `/rental-applications/${applicationId}/applicants`,
  );
  return data;
};

export const getRentalApplicant = async (
  applicationId: string,
  applicantId: string,
): Promise<RentalApplicant> => {
  const { data } = await axiosInstance.get<RentalApplicant>(
    `/rental-applications/${applicationId}/applicants/${applicantId}`,
  );
  return data;
};

export const fetchTruelayerTokenForRentalApplicant = ({
  applicantId,
  code,
  userId,
  applicationId,
}: {
  code: string;
  userId: string;
  applicantId: string;
  applicationId: string;
}): Promise<{ success: boolean; log_id: string }> =>
  axiosInstance
    .post(
      `/rental-applications/${applicationId}/applicants/${applicantId}/truelayer/oauth/callback`,
      { code, user_id: userId },
    )
    .then((res) => res.data);

export const generateTrueLayerIncomeVerificationUrlForRentalApplicant = ({
  applicationId,
  applicantId,
}: {
  applicationId: string;
  applicantId: string;
}): Promise<TruelayerAuthLinkResponse | undefined> =>
  axiosInstance
    .post(
      `/rental-applications/${applicationId}/applicants/${applicantId}/truelayer/bank-auth-link`,
    )
    .then((res) => res.data);

export const fetchTruelayerConnectedAccountsForRentalApplicant = ({
  applicationId,
  applicantId,
}: {
  applicationId: string;
  applicantId: string;
}): Promise<ConnectedAccount[]> =>
  axiosInstance
    .get(
      `/rental-applications/${applicationId}/applicants/${applicantId}/truelayer/connected-accounts`,
    )
    .then((res) => res.data);

export const updatePersonalDetails = (data: {
  applicantId: string;
  applicationId: string;
  details: {
    first_name?: string;
    last_name?: string;
    nationality?: string;
    mobile_number?: string;
    first_time_buyer?: boolean;
    moving_or_buying_another_home?: boolean;
    has_other_source_of_income?: boolean;
    entered_into_iva?: boolean;
    had_ccj_or_default?: boolean;
    missed_any_payments?: boolean;
    income?: number;
    completed_moved_in_demo?: boolean;
  };
}): Promise<GenericResponse> => {
  return axiosInstance
    .put(`/rental-applications/${data.applicationId}/applicants/${data.applicantId}`, data.details)
    .then((res) => res.data);
};

export const getRentalApplicantAddressHistory = (
  applicationId: string,
  applicantId: string,
): Promise<RentalApplicantAddresses[]> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/applicants/${applicantId}/addresses`)
    .then((res) => res.data);
};

export const submitRentalApplicantAddressHistory = (payload: {
  applicationId: string;
  applicantId: string;
  data: {
    street_address: string;
    city: string;
    country: string;
    move_in_date: string;
    post_code: string;
  };
}): Promise<any> => {
  return axiosInstance
    .post(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/addresses`,
      payload.data,
    )
    .then((res) => res.data);
};

export const updateRentalApplicantAddressHistory = (payload: {
  applicationId: string;
  applicantId: string;
  addressId: string;
  data: {
    street_address?: string;
    city?: string;
    country?: string;
    move_in_date?: string;
    post_code?: string;
  };
}): Promise<any> => {
  return axiosInstance
    .patch(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/addresses/${payload.addressId}`,
      payload.data,
    )
    .then((res) => res.data);
};

export const deleteRentalApplicantAddressHistory = (payload: {
  applicationId: string;
  applicantId: string;
  addressId: string;
}): Promise<any> => {
  return axiosInstance
    .delete(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/addresses/${payload.addressId}`,
    )
    .then((res) => res.data);
};

export const generateRentalIdentityVerificationUrl = ({
  applicationId,
  applicantId,
}: {
  applicationId: string;
  applicantId: string;
}): Promise<VerificationResponse & GenericResponse> => {
  return axiosInstance
    .post(
      `/rental-applications/${applicationId}/applicants/${applicantId}/identity-verification/generate-url`,
      { applicant_id: applicantId },
    )
    .then((res) => res.data);
};

export const getRentalApplicantIncome = (
  applicationId: string,
  applicantId: string,
): Promise<RentalApplicantIncome[]> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/applicants/${applicantId}/incomes`)
    .then((res) => res.data);
};

export const submitRentalApplicantIncome = (payload: {
  applicationId: string;
  applicantId: string;
  data: RentalApplicantIncome & { id?: string };
}): Promise<any> => {
  return axiosInstance
    .post(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/income`,
      payload.data,
    )
    .then((res) => res.data);
};

export const updateRentalApplicantIncome = (payload: {
  applicationId: string;
  applicantId: string;
  incomeId: string;
  data: RentalApplicantIncome;
}): Promise<any> => {
  return axiosInstance
    .patch(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/incomes/${payload.incomeId}`,
      payload.data,
    )
    .then((res) => res.data);
};

export const deleteRentalApplicantIncome = (payload: {
  applicationId: string;
  applicantId: string;
  incomeId: string;
}): Promise<any> => {
  return axiosInstance
    .delete(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/incomes/${payload.incomeId}`,
    )
    .then((res) => res.data);
};

export const submitRentalApplicantExpenditure = (payload: {
  applicationId: string;
  applicantId: string;
  data: RentalApplicantExpenditureFormData;
}): Promise<RentalApplicantExpenditureFormData & { id: string }> => {
  return axiosInstance
    .post(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/expenditure`,
      payload.data,
    )
    .then((res) => res.data);
};

export const updateRentalApplicantExpenditure = (payload: {
  applicationId: string;
  applicantId: string;
  data: RentalApplicantExpenditureFormData;
}): Promise<RentalApplicantExpenditureFormData> => {
  return axiosInstance
    .patch(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/expenditure`,
      payload.data,
    )
    .then((res) => res.data);
};

export const fetchRentalApplicantExpenditure = (payload: {
  applicationId: string;
  applicantId: string;
}): Promise<RentalApplicantExpenditureFormData & { id: string }> => {
  return axiosInstance
    .get(
      `/rental-applications/${payload.applicationId}/applicants/${payload.applicantId}/expenditure`,
    )
    .then((res) => res.data);
};

export const updateRentalApplication = (
  payload: UpdateRentalApplicationRequest,
): Promise<RentalApplicationEligibilityProps> => {
  return axiosInstance.patch(`/rental-applications/${payload.applicationId}`, payload.data);
};

export const addRentalApplicant = (payload: {
  applicationId: string;
  data: {
    first_name: string;
    last_name: string;
    email_address: string;
    mobile_number: string;
    income: number;
  };
}): Promise<RentalApplicant> => {
  return axiosInstance
    .post(`/rental-applications/${payload.applicationId}/applicant`, payload.data)
    .then((res) => res.data);
};

export const getViewings = async (
  applicationId: string,
  type: "UPCOMING" | "PAST",
): Promise<Viewing[]> => {
  const { data } = await axiosInstance.get<Viewing[]>(
    `/rental-applications/${applicationId}/viewings?type=${type}`,
  );
  return data;
};

export const rescheduleViewing = async (
  applicationId: string,
  viewingId: string,
  bookingDate: string,
): Promise<{ id: string; viewing_at: string; status: string; booking_rescheduled: boolean }> => {
  const { data } = await axiosInstance.post(
    `/rental-applications/${applicationId}/viewings/${viewingId}/reschedule`,
    { booking_date: bookingDate },
  );
  return data;
};

export const cancelViewing = async (
  applicationId: string,
  viewingId: string,
): Promise<{ id: string; status: string; booking_cancelled: boolean }> => {
  const { data } = await axiosInstance.post(
    `/rental-applications/${applicationId}/viewings/${viewingId}/cancel`,
  );
  return data;
};

export const reserveProperty = async (
  applicationId: string,
  mondayPropertyId: string,
  date?: string,
): Promise<{ success: true }> => {
  const { data } = await axiosInstance.post<{ success: true }>(
    `/rental-applications/${applicationId}/reserve-property`,
    { mondayPropertyId, moveInDate: date },
  );
  return data;
};

export const getReservedProperty = async (applicationId: string) => {
  const { data } = await axiosInstance.get(
    `/rental-applications/${applicationId}/reserved-property`,
  );
  return data;
};

export const checkEmailAvailability = (
  email: string,
): Promise<{ email_already_exists: boolean }> => {
  return axiosInstance
    .get(`/rental-applications/email-availability/${email}`)
    .then((res) => res.data);
};

export const checkPhoneNumberAvailability = (
  phoneNumber: string,
): Promise<{ phone_already_exists: boolean }> => {
  return axiosInstance
    .get(`/rental-applications/phone-availability/${phoneNumber}`)
    .then((res) => res.data);
};

export const createRentalApplication = (payload: CreateRentalApplicantRequest) => {
  return axiosInstance.post(`/rental-applications`, payload).then((res) => res.data);
};
export const submitRentalApplication = (applicationId: string): Promise<RentalApplication> => {
  return axiosInstance.post(`/rental-applications/${applicationId}/submit`).then((res) => res.data);
};

export const deleteRentalApplicantDocument = ({
  applicationId,
  applicantId,
  documentId,
}: {
  applicationId: string;
  applicantId: string;
  documentId: string;
}): Promise<GenericResponse> => {
  return axiosInstance
    .delete(
      `/rental-applications/${applicationId}/applicants/${applicantId}/documents/${documentId}`,
    )
    .then((res) => res.data);
};

export const getRentalApplicantDocuments = ({
  applicationId,
  applicantId,
  type,
}: {
  applicationId: string;
  applicantId: string;
  type: "INCOME" | "IDENTITY_VERIFICATION";
}): Promise<{ name: string; id: string }[]> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/applicants/${applicantId}/documents`, {
      params: { type },
    })
    .then((res) => res.data);
};

export const requestUploadSession = ({
  applicationId,
  applicantId,
}: {
  applicationId: string;
  applicantId: string;
}): Promise<{ folderId: string; temporaryToken: string }> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/applicants/${applicantId}/box/session`)
    .then((res) => res.data);
};

export const addDocuments = ({
  applicationId,
  applicantId,
  documents,
  session_token,
  type,
}: {
  applicationId: string;
  applicantId: string;
  session_token: string;
  documents: { name: string; id: string }[];
  type: "INCOME" | "IDENTITY_VERIFICATION";
}): Promise<{ name: string; id: string }[]> => {
  const body = {
    documents,
    token: session_token,
    type,
  };
  return axiosInstance
    .post(`/rental-applications/${applicationId}/applicants/${applicantId}/documents`, body)
    .then((res) => res.data);
};

export const getRentalApplicationMoveInSummary = ({
  applicationId,
}: {
  applicationId: string;
}): Promise<RentalApplicationMoveInSummary> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/payment/summary`)
    .then((res) => res.data);
};

export const getVideoGuides = ({
  slug,
  limit,
}: {
  slug: string;
  limit?: number;
}): Promise<VideoGuide[]> => {
  return axiosInstance
    .get("/cms/developments/video-guides", { params: { slug, limit } })
    .then((res) => res.data);
};

export const getRentalApplicationDocuments = ({
  applicationId,
}: {
  applicationId: string;
}): Promise<RentalApplicationDocument[]> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/documents`)
    .then((res) => res.data);
};

export const getRentalApplicationDocumentDownloadUrl = ({
  applicationId,
  documentId,
}: {
  applicationId: string;
  documentId: string;
}): Promise<{ url: string }> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/documents/${documentId}/download`)
    .then((res) => res.data);
};

export const getRentalApplicationOfferDetails = ({
  applicationId,
}: {
  applicationId: string;
}): Promise<RentalApplicationOfferDetails> => {
  return axiosInstance.get(`/rental-applications/${applicationId}/offer`).then((res) => res.data);
};

export const addWitnessToRentalApplication = ({
  applicationId,
  data,
}: {
  applicationId: string;
  data: {
    full_name: string;
    email: string;
  };
}): Promise<{ full_name: string; email: string }> => {
  return axiosInstance
    .put(`/rental-applications/${applicationId}/offer/confirm-witness`, data)
    .then((res) => res.data);
};

export const getRentalApplicantIdentityVerification = ({
  applicationId,
  applicantId,
}: {
  applicationId: string;
  applicantId: string;
}): Promise<RentalApplicantIdentityVerification> => {
  return axiosInstance
    .get(`/rental-applications/${applicationId}/applicants/${applicantId}/identity-verification`)
    .then((res) => res.data);
};

export const addRentalApplicantRentShareCode = ({
  applicationId,
  applicantId,
  data,
}: {
  applicationId: string;
  applicantId: string;
  data: {
    rent_share_code: string;
  };
}): Promise<{ rent_share_code: string }> => {
  return axiosInstance
    .post(
      `/rental-applications/${applicationId}/applicants/${applicantId}/identity-verification/rent-share-code`,
      data,
    )
    .then((res) => res.data);
};

export default axiosInstance;
